import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { load } from "js-yaml";

const DEFAULT_LANGUAGE = "en";
const SUPPORTED_LANGUAGES = ["en", "de"];

const I18nContext = createContext({
  language: DEFAULT_LANGUAGE,
  t: (key) => key,
  get: () => undefined,
  ready: false,
});

function deepGet(source, path) {
  return path.split(".").reduce((acc, segment) => {
    if (acc == null) return undefined;
    return acc[segment];
  }, source);
}

function interpolate(template, values) {
  if (typeof template !== "string") return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    if (values[key] == null) return `{${key}}`;
    return String(values[key]);
  });
}

async function loadLocale(language) {
  const response = await fetch(`/locales/${language}.yaml`);
  if (!response.ok) {
    throw new Error(`Could not load locale: ${language}`);
  }

  const fileContent = await response.text();
  const parsed = load(fileContent);
  return parsed && typeof parsed === "object" ? parsed : {};
}

export function I18nProvider({ language, children }) {
  const [locales, setLocales] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const loaded = await Promise.all(
        SUPPORTED_LANGUAGES.map(async (lang) => {
          const data = await loadLocale(lang);
          return [lang, data];
        })
      );

      if (cancelled) return;
      setLocales(Object.fromEntries(loaded));
      setReady(true);
    }

    bootstrap().catch(() => {
      if (cancelled) return;
      setLocales({});
      setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const dictionary = useMemo(() => {
    return locales[language] || locales[DEFAULT_LANGUAGE] || {};
  }, [language, locales]);

  const get = useCallback(
    (key) => {
      return deepGet(dictionary, key);
    },
    [dictionary]
  );

  const t = useCallback(
    (key, values = {}) => {
      const localized = deepGet(dictionary, key);
      if (typeof localized === "string") {
        return interpolate(localized, values);
      }

      if (localized != null) {
        return localized;
      }

      const fallback = deepGet(locales[DEFAULT_LANGUAGE] || {}, key);
      if (typeof fallback === "string") {
        return interpolate(fallback, values);
      }

      if (fallback != null) {
        return fallback;
      }

      return key;
    },
    [dictionary, locales]
  );

  const value = useMemo(
    () => ({
      language,
      t,
      get,
      ready,
      languages: SUPPORTED_LANGUAGES,
    }),
    [language, t, get, ready]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
